/**
 * Workshops API - Mudo Network
 * 
 * Functions for querying and managing workshop data from Supabase.
 * Workshops are bookable ceramic classes/events hosted by studios.
 */

import { supabase } from "../../supabaseClient";
import type { Database } from "../database.types";
import { v4 as uuidv4 } from "uuid";
import { getCachedSession } from '../../contexts/AuthContext';

type Workshop = Database["public"]["Tables"]["workshops"]["Row"];
type WorkshopInsert = Database["public"]["Tables"]["workshops"]["Insert"];

/**
 * SINGLE SOURCE OF TRUTH: Save workshop to Supabase
 * 
 * Guarantees:
 * - Stable ID (generates UUID if missing, never changes)
 * - Upsert with onConflict (UPDATE if exists, INSERT if new)
 * - Returns authoritative Supabase row
 * - ⚠️ VALIDATES: start_at must exist (computes end_at from duration if needed)
 *   If missing, throws error BEFORE calling Supabase (prevents 23502 NOT NULL violation)
 * 
 * IMPORTANT: Draft saves (missing dates) should NOT call this.
 * Use this only when workshop has date/time and duration information.
 * 
 * @param workshop - Workshop object (with or without id)
 * @returns Authoritative workshop row from Supabase
 * @throws Error if start_at is missing (NOT NULL constraint)
 */
export async function saveWorkshopSupabase(workshop: any): Promise<Workshop> {
  // 🔒 VALIDATION: Must have start_at (with start_at or startISO)
  const startAt = workshop.start_at ?? workshop.startISO;
  if (!startAt) {
    throw new Error(
      "[saveWorkshopSupabase] Cannot save to Supabase: start_at is required. " +
      "Please set a workshop date and time before saving."
    );
  }

  // 🔒 VALIDATION: If end_at missing, compute from startISO + duration_minutes
  let endAt = workshop.end_at;
  if (!endAt && startAt && (workshop.duration_minutes || workshop.durationMinutes)) {
    try {
      const duration = Number(workshop.duration_minutes ?? workshop.durationMinutes ?? 120);
      const startDate = new Date(startAt);
      const endDate = new Date(startDate.getTime() + duration * 60_000);
      endAt = endDate.toISOString();
      console.log('[workshopsApi.saveWorkshopSupabase] Computed end_at from duration', {
        startAt,
        duration_minutes: duration,
        end_at: endAt,
      });
    } catch (e) {
      console.error('[workshopsApi.saveWorkshopSupabase] Failed to compute end_at', { error: e });
      // Fallback: require explicit end_at if computation fails
      throw new Error(
        "[saveWorkshopSupabase] Cannot compute end_at from duration. " +
        "Please ensure startISO and duration_minutes are valid numbers."
      );
    }
  }

  // If we still don't have end_at, block the save
  if (!endAt) {
    throw new Error(
      "[saveWorkshopSupabase] Cannot save to Supabase: end_at is required. " +
      "Workshop duration must be set (duration_minutes) or provide explicit end_at."
    );
  }

  const id = workshop.id || uuidv4();

  // ✅ CRITICAL: workshop_key is NOT NULL in schema - always set it
  const workshop_key = workshop.workshop_key || id;

  // 💰 Handle price conversion: price (in dollars) → price_cents (in cents)
  let priceCents = 0;
  if (workshop.price_cents !== undefined && workshop.price_cents !== null) {
    // Already in cents
    priceCents = Number(workshop.price_cents) || 0;
  } else if (workshop.priceCents !== undefined && workshop.priceCents !== null) {
    // Camel case version (cents)
    priceCents = Number(workshop.priceCents) || 0;
  } else if (workshop.price !== undefined && workshop.price !== null) {
    // In dollars - convert to cents
    priceCents = Math.round(Number(workshop.price) * 100) || 0;
  }
  // Default to 0 if no price found

  const payload = {
    id,
    workshop_key,
    studio_id: workshop.studio_id,
    title: workshop.title ?? "",
    description: workshop.description ?? null,
    type: workshop.type ?? null,
    skill_level: workshop.skill_level ?? null,
    start_at: startAt,
    end_at: endAt,
    duration_minutes: workshop.duration_minutes ?? null,
    price_cents: priceCents,
    currency: workshop.currency ?? "AUD",
    capacity: workshop.capacity ?? null,
    is_published: !!workshop.is_published,
    updated_at: new Date().toISOString(),
  };

  console.log('[workshopsApi.saveWorkshopSupabase] Saving workshop:', {
    operation: workshop.id ? 'UPDATE' : 'INSERT',
    id: payload.id,
    studio_id: payload.studio_id,
    title: payload.title,
    is_published: payload.is_published,
    price_cents: payload.price_cents,
    start_at: payload.start_at,
    end_at: payload.end_at,
  });

  const { data, error } = await supabase
    .from("workshops")
    .upsert(payload, { onConflict: "id" })
    .select("*")
    .single();

  if (error) {
    console.error('[workshopsApi.saveWorkshopSupabase] FAILED:', error);
    throw error;
  }

  console.log('[workshopsApi.saveWorkshopSupabase] SUCCESS:', {
    id: data.id,
    created_at: data.created_at,
    updated_at: data.updated_at,
    price_cents: data.price_cents
  });

  return data;
}

function logSupabaseError(functionName, queryDesc, error) {
  if (!error) return;
  console.error(
    `[workshopsApi.${functionName}]`,
    {
      query: queryDesc,
      errorCode: error?.code ?? error?.status,
      message: error?.message,
    },
    error
  );
}

async function attachStudiosToWorkshops(workshops: Workshop[] = []) {
  if (!Array.isArray(workshops) || workshops.length === 0) {
    return workshops;
  }
  const studioIds = [...new Set(workshops.map((w) => w.studio_id).filter(Boolean))];
  if (studioIds.length === 0) {
    return workshops.map((w) => ({ ...w, studio: null }));
  }

  const queryDesc = `select id, name, city, country, slug, stripe_account_id_resolved from studios where id in (${studioIds.join(
    ", "
  )})`;
  try {
    const { data: studios, error } = await supabase
      .from("studios")
      .select("id, name, city, country, slug, stripe_account_id_resolved")
      .in("id", studioIds);

    if (error) {
      logSupabaseError("attachStudiosToWorkshops", queryDesc, error);
      return workshops.map((w) => ({ ...w, studio: null }));
    }

    const studioMap = new Map((studios || []).map((s) => [s.id, s]));
    return workshops.map((w) => ({
      ...w,
      studio: w.studio_id ? studioMap.get(w.studio_id) || null : null,
    }));
  } catch (error) {
    logSupabaseError("attachStudiosToWorkshops", queryDesc, error);
    return workshops.map((w) => ({ ...w, studio: null }));
  }
}

/**
 * List public workshops with optional filters
 * 
 * @param params - Search parameters
 * @param params.city - Filter by studio city
 * @param params.studioId - Filter by specific studio
 * @param params.from - Filter workshops starting after this date
 * @param params.to - Filter workshops starting before this date
 * @param params.skillLevel - Filter by skill level ('Beginner', 'Intermediate', 'Advanced')
 * @param params.limit - Max results (default: 50)
 * @returns Array of workshops with studio information
 * 
 * @example
 * const { data: workshops } = await listPublicWorkshops({
 *   city: 'Sydney',
 *   from: '2025-01-01',
 *   to: '2025-12-31',
 *   skillLevel: 'Beginner'
 * });
 */
export async function getVisitorWorkshops({
  city,
  studioId,
  from,
  to,
  skillLevel,
  limit = 50,
}: {
  city?: string;
  studioId?: string;
  from?: string; // ISO date string
  to?: string; // ISO date string
  skillLevel?: string;
  limit?: number;
} = {}) {
  const fromDate = from || new Date().toISOString();
  const queryDesc = `supabase.from('workshops').select with studio join WHERE is_published=true AND archived_at IS NULL AND start_at >= ${fromDate}`;

  try {
    let query = supabase
      .from("workshops")
      .select(`
        id,
        title,
        description,
        skill_level,
        start_at,
        end_at,
        price_cents,
        currency,
        capacity,
        seats_taken,
        hero_image_url,
        media_gallery,
        is_published,
        created_at,
        updated_at,
        studio_id,
        studios:studio_id (
          id,
          name,
          city,
          country,
          slug,
          stripe_account_id_resolved
        )
      `)
      .eq("is_published", true)
      .is("archived_at", null)
      .is("cancelled_at", null)
      .gte("start_at", fromDate)
      .order("start_at", { ascending: true })
      .limit(limit);

    if (studioId) {
      query = query.eq("studio_id", studioId);
    }
    if (to) {
      query = query.lte("start_at", to);
    }
    if (skillLevel) {
      query = query.eq("skill_level", skillLevel);
    }

    const { data: workshops, error: workshopsErr } = await query;
    if (workshopsErr) {
      throw workshopsErr;
    }

    // Normalize studio reference to match expected shape
    const normalized = (workshops || []).map((w) => ({
      ...w,
      studio: w.studios || null,
    }));

    if (city) {
      return {
        data: (normalized || []).filter(
          (w) => String(w.studio?.city || "").toLowerCase() === city.toLowerCase()
        ),
        error: null,
      };
    }

    return { data: normalized, error: null };
  } catch (error) {
    logSupabaseError("getVisitorWorkshops", queryDesc, error);
    return { data: null, error };
  }
}

/**
 * Get a workshop by ID with studio details
 * 
 * @param id - Workshop UUID
 * @returns Workshop object with studio information
 * 
 * @example
 * const { data: workshop } = await getWorkshopById('workshop-uuid');
 */
export async function getWorkshopById(id: string) {
  try {
    const { data, error } = await supabase
      .from('workshops')
      .select(`
        *,
        studio:studios!workshops_studio_id_fkey (
          id,
          name,
          city,
          country,
          stripe_account_id
        )
      `)
      .eq('id', id)
      .eq('is_published', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return { data: null, error: null };
      }

      // Fallback: fetch workshop and studio separately
      console.warn('[workshopsApi] Nested select failed for workshop, using fallback:', error);
      const { data: workshop, error: workshopErr } = await supabase
        .from('workshops')
        .select('*')
        .eq('id', id)
        .eq('is_published', true)
        .single();

      if (workshopErr) {
        if (workshopErr.code === 'PGRST116') {
          return { data: null, error: null };
        }
        throw workshopErr;
      }

      if (workshop?.studio_id) {
        const { data: studio } = await supabase
          .from('studios')
          .select('id, name, city, country, stripe_account_id_resolved')
          .eq('id', workshop.studio_id)
          .single();

        return { data: { ...workshop, studio: studio || null }, error: null };
      }

      return { data: workshop, error: null };
    }

    return { data, error: null };
  } catch (error) {
    console.error(`Error fetching workshop by id "${id}":`, error);
    return { data: null, error };
  }
}

/**
 * List upcoming workshops (starting from now)
 * 
 * @param params - Optional parameters
 * @param params.limit - Max results (default: 20)
 * @returns Array of upcoming workshops
 * 
 * @example
 * const { data: upcoming } = await listUpcomingWorkshops({ limit: 10 });
 */
export async function listUpcomingWorkshops({ limit = 20 }: { limit?: number } = {}) {
  try {
    const now = new Date().toISOString();

    // Fetch workshops without embedded studio join (avoid PGRST200)
    const { data: workshops, error: workshopsErr } = await supabase
      .from('workshops')
      .select('*')
      .eq('is_published', true)
      .is('archived_at', null)
      .gte('start_at', now)
      .order('start_at', { ascending: true })
      .limit(limit);

    if (workshopsErr) throw workshopsErr;

    // Fetch studios for all workshops in a separate query
    const studioIds = [...new Set((workshops || []).map(w => w.studio_id).filter(Boolean))];
    if (studioIds.length > 0) {
      const { data: studios } = await supabase
        .from('studios')
        .select('id, name, city, country, stripe_account_id_resolved')
        .in('id', studioIds);

      const studioMap = new Map((studios || []).map(s => [s.id, s]));
      const merged = (workshops || []).map(w => ({
        ...w,
        studio: w.studio_id ? studioMap.get(w.studio_id) || null : null,
      }));
      return { data: merged, error: null };
    }

    // No studios to fetch, return workshops with studio: null
    return { data: (workshops || []).map(w => ({ ...w, studio: null })), error: null };
  } catch (error) {
    console.error('Error fetching upcoming workshops:', error);
    return { data: null, error };
  }
}

/**
 * List ALL workshops for a studio (including drafts/unpublished)
 * For use by studio owners in their management dashboard
 * 
 * @param studioId - Studio UUID
 * @param includeArchived - Whether to include archived workshops (default: false)
 * @returns Array of all workshops for this studio
 * 
 * @example
 * const { data: myWorkshops } = await listWorkshopsForStudio('my-studio-id');
 */
export async function getStudioWorkshops(
  studioId: string,
  {
    includeArchived = false,
    from,
    to,
    limit = 200,
  }: {
    includeArchived?: boolean;
    from?: string;
    to?: string;
    limit?: number;
  } = {}
) {
  if (!studioId) {
    const err = new Error("Missing studioId");
    logSupabaseError(
      "getStudioWorkshops",
      "supabase.from('workshops') with missing studioId",
      err
    );
    return { data: null, error: err };
  }

  const queryDesc = `supabase.from('workshops').select('*') WHERE studio_id=${studioId}${
    includeArchived ? "" : " AND archived_at IS NULL"
  } ORDER BY start_at ASC LIMIT ${limit}`;

  try {
    let query = supabase
      .from("workshops")
      .select("*")
      .eq("studio_id", studioId)
      .order("start_at", { ascending: true })
      .limit(limit);

    if (!includeArchived) {
      query = query.is("archived_at", null);
    }
    if (from) query = query.gte("start_at", from);
    if (to) query = query.lte("start_at", to);

    const { data, error } = await query;

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    logSupabaseError("getStudioWorkshops", queryDesc, error);
    return { data: null, error };
  }
}

/**
 * List all workshops for the currently logged-in user's studio
 * Uses the current session to identify the studio
 * 
 * @returns Array of all workshops owned by current user's studio
 * 
 * @example
 * const { data: myWorkshops } = await listWorkshopsForCurrentStudio();
 */
export async function listWorkshopsForCurrentStudio() {
  try {
    // Get current session from centralized auth state (no direct supabase.auth call)
    const sessionData = getCachedSession();
    const user = sessionData?.user || null;
    
    if (!user) {
      console.warn('[workshopsApi.listWorkshopsForCurrentStudio] No authenticated user');
      return { data: [], error: new Error('Not authenticated') };
    }

    // Workshops are owned via workshops.studio_id -> studios.owner_user_id
    const { data: studio, error: studioErr } = await supabase
      .from("studios")
      .select("id")
      .eq("owner_user_id", user.id)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    if (studioErr) throw studioErr;
    if (!studio?.id) {
      return { data: [], error: new Error("No studio found for current user") };
    }

    const { data, error } = await supabase
      .from("workshops")
      .select("*")
      .eq("studio_id", studio.id)
      .order("start_at", { ascending: true });

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching workshops for current studio:', error);
    return { data: null, error };
  }
}

/**
 * Create a new workshop for a specific studio
 * Convenience wrapper around createWorkshop with studio_id set
 * 
 * @param studioId - Studio UUID
 * @param workshop - Workshop data (studio_id will be set automatically)
 * @returns Created workshop
 * 
 * @example
 * const { data } = await createWorkshopForStudio('my-studio-id', {
 *   title: 'Wheel Throwing 101',
 *   description: 'Learn to throw on the wheel',
 *   start_at: '2025-03-15T14:00:00Z',
 *   end_at: '2025-03-15T17:00:00Z',
 *   price_cents: 15000,
 *   capacity: 6
 * });
 */
export async function createWorkshopForStudio(
  studioId: string,
  workshop: Omit<WorkshopInsert, 'studio_id'>
) {
  return createWorkshop({
    ...workshop,
    studio_id: studioId,
  });
}

/**
 * Check if a workshop has available seats
 * 
 * @param workshopId - Workshop UUID
 * @returns Object with availability info
 * 
 * @example
 * const { data } = await checkWorkshopAvailability('workshop-uuid');
 * // { available: true, seatsRemaining: 5, capacity: 10, seatsTaken: 5 }
 */
export async function checkWorkshopAvailability(workshopId: string) {
  try {
    const { data, error } = await supabase
      .from('workshops')
      .select('capacity, seats_taken')
      .eq('id', workshopId)
      .single();

    if (error) throw error;

    const seatsRemaining = data.capacity - data.seats_taken;
    const available = seatsRemaining > 0;

    return {
      data: {
        available,
        seatsRemaining,
        capacity: data.capacity,
        seatsTaken: data.seats_taken,
      },
      error: null,
    };
  } catch (error) {
    console.error('Error checking workshop availability:', error);
    return { data: null, error };
  }
}

/**
 * Create a new workshop (for authenticated studio owners)
 * 
 * @param workshop - Workshop data
 * @returns Created workshop
 * 
 * @example
 * const { data } = await createWorkshop({
 *   studio_id: 'studio-uuid',
 *   title: 'Hand-building Basics',
 *   description: 'Learn the fundamentals...',
 *   start_at: '2025-02-01T10:00:00Z',
 *   end_at: '2025-02-01T13:00:00Z',
 *   price_cents: 12000,
 *   capacity: 8,
 *   skill_level: 'Beginner'
 * });
 */
export async function createWorkshop(workshop: WorkshopInsert) {
  try {
    // Validate studio_id is present
    if (!workshop.studio_id) {
      throw new Error('Missing studio_id - cannot create workshop without studio association');
    }

    // Get current session to ensure auth token is present (from centralized auth state)
    const session = getCachedSession();
    
    if (import.meta.env.DEV) {
      console.log('[workshopsApi.createWorkshop] Auth check:', {
        hasSession: !!session,
        studioId: workshop.studio_id,
        workshopTitle: workshop.title
      });
    }

    if (!session) {
      throw new Error('No authenticated session - user must be logged in to create workshops');
    }

    // Verify studio exists before attempting insert
    const { data: studioExists, error: studioCheckError } = await supabase
      .from('studios')
      .select('id')
      .eq('id', workshop.studio_id)
      .limit(1);
    
    if (studioCheckError) {
      console.error('[workshopsApi.createWorkshop] Error checking studio:', studioCheckError);
      throw new Error(`Failed to verify studio exists: ${studioCheckError.message}`);
    }
    
    if (!studioExists || studioExists.length === 0) {
      const err = new Error(`Studio with ID "${workshop.studio_id}" not found in database. Foreign key constraint will fail.`);
      console.error('[workshopsApi.createWorkshop] Studio not found:', {
        studioId: workshop.studio_id,
        studioExists: studioExists?.length || 0
      });
      throw err;
    }
    
    console.log('[workshopsApi.createWorkshop] Studio verified:', { studioId: workshop.studio_id });

    const { data, error } = await supabase
      .from('workshops')
      .insert(workshop)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error: any) {
    // RLS permission errors (403) are expected when user doesn't have studio membership permissions
    // Local save will still succeed via Drive/Folder, so log as warning not error
    if (error?.code === '42501' || error?.status === 403) {
      console.warn('[workshopsApi] Supabase RLS permission denied (expected if no studio membership):', error?.message);
    } else {
      console.error('Error creating workshop:', error);
    }
    return { data: null, error };
  }
}

/**
 * Update a workshop (for authenticated studio owners)
 * 
 * @param id - Workshop UUID
 * @param updates - Fields to update
 * @returns Updated workshop
 * 
 * @example
 * const { data } = await updateWorkshop('workshop-uuid', {
 *   capacity: 10,
 *   is_published: true
 * });
 */
export async function updateWorkshop(
  id: string,
  updates: Database['public']['Tables']['workshops']['Update']
) {
  try {
    // Get current session to ensure auth token is present (from centralized auth state)
    const session = getCachedSession();
    
    if (import.meta.env.DEV) {
      console.log('[workshopsApi.updateWorkshop] Auth check:', {
        hasSession: !!session,
        workshopId: id,
        updatingFields: Object.keys(updates)
      });
    }

    if (!session) {
      throw new Error('No authenticated session - user must be logged in to update workshops');
    }

    const { data, error } = await supabase
      .from('workshops')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error updating workshop:', error);
    return { data: null, error };
  }
}

/**
 * Toggle publish status via RPC to enforce validation and side effects
 */
export async function setWorkshopPublish(id: string, publish: boolean) {
  try {
    // Get current session from centralized auth state (no direct supabase.auth call)
    const session = getCachedSession();

    if (!session) {
      throw new Error('No authenticated session - user must be logged in to publish/unpublish workshops');
    }

    const { data, error } = await supabase.rpc('set_workshop_publish', {
      p_workshop_id: id,
      p_publish: publish,
    });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[workshopsApi.setWorkshopPublish] Failed to toggle publish', error);
    return { data: null, error };
  }
}

export async function publishWorkshop(id: string) {
  const queryDesc = `rpc set_workshop_publish(p_workshop_id=${id}, p_publish=true)`;
  try {
    const { data, error } = await setWorkshopPublish(id, true);
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    logSupabaseError("publishWorkshop", queryDesc, error);
    return { data: null, error };
  }
}

export async function unpublishWorkshop(id: string) {
  const queryDesc = `rpc set_workshop_publish(p_workshop_id=${id}, p_publish=false)`;
  try {
    const { data, error } = await setWorkshopPublish(id, false);
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    logSupabaseError("unpublishWorkshop", queryDesc, error);
    return { data: null, error };
  }
}

/**
 * Delete a workshop (archives it via RPC)
 * 
 * GHOST WORKSHOP FIX: After database delete, also purges:
 * - workshops/workshops.json (localStorage)
 * - Local studio workshop caches
 * - Broadcasts workshops:changed event
 * 
 * @param id - Workshop UUID
 * @returns Archived workshop row
 */
export async function archiveWorkshop(id: string) {
  if (!id) {
    return { data: null, error: new Error("[HARDENED] archiveWorkshop requires a valid workshop ID") };
  }

  const queryDesc = `archiveWorkshop(id=${id})`;
  try {
    // 🔒 HARDENED DELETE: Use deterministic pattern with verification
    // Must return the deleted row to confirm it actually deleted something
    // Prevents silently failing if workshop doesn't exist or RLS blocks access
    
    console.log("[archiveWorkshop] Setting archived_at (soft delete)", { workshopId: id });

    const { data, error } = await supabase
      .from("workshops")
      .update({ archived_at: new Date().toISOString() })
      .eq("id", id)
      .select("id, title, archived_at, studio_id")
      .single();

    if (error) {
      console.error("[archiveWorkshop] FAILED to set archived_at", { workshopId: id, error: error?.message });
      throw error;
    }

    // Verify that the update actually affected a row
    if (!data?.id) {
      throw new Error(
        `[HARDENED] Archive matched 0 rows. Workshop "${id}" may not exist, ` +
        `or RLS policies may be blocking access. No deletion occurred.`
      );
    }

    console.log("[archiveWorkshop] Archive successful (data modified in DB)", {
      workshopId: id,
      title: data?.title,
      archived_at: data?.archived_at,
    });

    // ✅ GHOST WORKSHOP FIX: Purge caches so deleted workshop doesn't reappear
    try {
      // Purge from workshops/workshops.json (localStorage cache)
      const lsKey = "mudo:workshops/workshops.json";
      const lsData = localStorage.getItem(lsKey);
      if (lsData) {
        try {
          const list = JSON.parse(lsData);
          if (Array.isArray(list)) {
            const before = list.length;
            const filtered = list.filter((w) => w.id !== id);
            if (filtered.length < before) {
              localStorage.setItem(lsKey, JSON.stringify(filtered));
              console.log("[archiveWorkshop] Purged from workshops.json cache", {
                workshopId: id,
                before,
                after: filtered.length,
              });
            }
          }
        } catch (e) {
          console.warn("[archiveWorkshop] Failed to purge workshops.json cache", e?.message);
        }
      }

      // Purge from studio-specific cache (if exists)
      if (data?.studio_id) {
        const studioCacheKey = `workshops:${data.studio_id}`;
        const studioCacheData = localStorage.getItem(studioCacheKey);
        if (studioCacheData) {
          try {
            const list = JSON.parse(studioCacheData);
            if (Array.isArray(list)) {
              const before = list.length;
              const filtered = list.filter((w) => w.id !== id);
              if (filtered.length < before) {
                localStorage.setItem(studioCacheKey, JSON.stringify(filtered));
                console.log("[archiveWorkshop] Purged from studio cache", {
                  workshopId: id,
                  studioId: data.studio_id,
                  before,
                  after: filtered.length,
                });
              }
            }
          } catch (e) {
            console.warn("[archiveWorkshop] Failed to purge studio cache", e?.message);
          }
        }
      }

      // Broadcast event so visitor list refreshes immediately
      if (typeof window !== "undefined") {
        const event = new CustomEvent("workshops:changed", {
          detail: { studioId: data?.studio_id, workshopId: id, action: "archived" },
          bubbles: true,
        });
        window.dispatchEvent(event);
        console.log("[archiveWorkshop] Broadcast workshops:changed event", {
          workshopId: id,
          studioId: data?.studio_id,
        });
      }
    } catch (cacheErr) {
      // Cache purge failures are non-critical; log but don't throw
      console.warn("[archiveWorkshop] Cache purge error (continuing)", cacheErr?.message);
    }

    return { data, error: null };
  } catch (error: any) {
    logSupabaseError("archiveWorkshop", queryDesc, error);
    return { data: null, error };
  }
}

export async function deleteWorkshop(id: string) {
  return archiveWorkshop(id);
}

/**
 * Sync (upsert) a local studio workshop record to the network (Supabase) workshops table.
 *
 * This maps the flexible local/studio-side workshop shape into the canonical
 * network schema and performs either an update (if networkId provided) or an insert.
 *
 * Local shape (partial, flexible):
 * {
 *   id, title, description, startISO, date, time, durationMins,
 *   price, capacity, level, teaserDataUrl, isPublic, networkId,
 * }
 *
 * Network shape (subset):
 *   title, description, start_at, end_at, price_cents, capacity,
 *   skill_level, hero_image_url, is_published, studio_id
 *
 * @param local Workshop-like object from Studio UI
 * @param opts Optional studio/user context
 * @returns { data, error, mode } where mode = 'create' | 'update'
 */
export async function upsertWorkshop(
  local: any,
  opts: { studioProfile?: { id?: string; user_id?: string }; fallbackStudioId?: string } = {}
) {
  try {
    if (!supabase) throw new Error('Supabase client not initialized');

    // Get current session to ensure auth token is present (from centralized auth state)
    const session = getCachedSession();
    
    if (import.meta.env.DEV) {
      console.log('[workshopsApi.upsertWorkshop] Auth check:', {
        hasSession: !!session,
        workshopTitle: local?.title
      });
    }

    if (!session) {
      return { 
        data: null, 
        error: new Error('No authenticated session - user must be logged in to sync workshops'), 
        mode: 'skip' 
      };
    }

    // Resolve studio_id to attach to workshop
    let studioId = opts?.studioProfile?.id || opts?.fallbackStudioId || local?.studioId || local?.studio_id;
    
    // Fallback: query studios table by owner_user_id if still missing
    if (!studioId && session?.user?.id) {
      try {
        const { data: studios, error: studioError } = await supabase
          .from('studios')
          .select('id')
          .eq('owner_user_id', session.user.id)
          .limit(1);
        
        if (!studioError && studios?.length > 0) {
          studioId = studios[0].id;
          console.log('[workshopsApi.upsertWorkshop] Resolved studio_id from owner_user_id:', studioId);
        }
      } catch (err) {
        console.warn('[workshopsApi.upsertWorkshop] Failed to query studios:', err);
      }
    }
    
    if (import.meta.env.DEV) {
      console.log('[workshopsApi.upsertWorkshop] Studio ID check:', {
        studioId,
        hasStudioId: !!studioId,
        userId: session?.user?.id
      });
    }
    
    if (!studioId) {
      return { data: null, error: new Error('Missing studio_id - workshop sync requires studio association. Could not resolve from studioProfile or studios table.'), mode: 'skip' };
    }

    // Derive start ISO
    let startISO: string | null = local?.startISO || null;
    if (!startISO && local?.date && local?.time) {
      try { startISO = new Date(`${local.date}T${local.time}:00`).toISOString(); } catch { /* ignore */ }
    }
    if (!startISO) {
      // Schedule 1 hour in future as fallback
      startISO = new Date(Date.now() + 60 * 60 * 1000).toISOString();
    }

    // Validate start_at is a valid ISO date
    try {
      new Date(startISO);
    } catch {
      throw new Error(`Invalid start_at date: "${startISO}"`);
    }

    // Compute end_at from durationMins if available
    let endISO: string | null = startISO;
    const durationMins = Number(local?.durationMins || 0);
    if (startISO && durationMins > 0) {
      endISO = new Date(new Date(startISO).getTime() + durationMins * 60000).toISOString();
    }
    
    // Ensure end_at is set and valid
    if (!endISO) {
      endISO = startISO; // Fallback: same as start_at
    }
    
    // Validate end_at is after start_at
    if (new Date(endISO).getTime() < new Date(startISO).getTime()) {
      throw new Error(`end_at (${endISO}) must be after or equal to start_at (${startISO})`);
    }

    // Price mapping (ensure integer cents)
    const rawPrice = Number(local?.price || local?.priceEach || 0);
    const price_cents = Math.max(0, Math.round(rawPrice * 100));

    // Visibility mapping
    const isPublished = !!(local?.isPublic || local?.visibleToVisitors || local?.publishToMudo);

    const payload: any = {
      title: local?.title || 'Untitled Workshop',
      description: local?.description || '',
      start_at: startISO,
      end_at: endISO,
      price_cents,
      capacity: Number(local?.capacity || 0) || 0,
      skill_level: local?.level || local?.skill_level || null,
      hero_image_url: local?.teaserDataUrl || local?.hero_image_url || null,
      studio_id: studioId,
    };

    console.log('[workshopsApi.upsertWorkshop] Payload', {
      ...payload,
      studioId,
      hasValidStudioId: !!studioId && typeof studioId === 'string' && studioId.length > 0,
      start_at_valid: !!payload.start_at && !isNaN(new Date(payload.start_at).getTime()),
      end_at_valid: !!payload.end_at && !isNaN(new Date(payload.end_at).getTime()),
      dates_ordered: new Date(payload.end_at).getTime() >= new Date(payload.start_at).getTime(),
    });

    // Verify studio_id is valid before attempting insert
    if (!studioId || typeof studioId !== 'string' || studioId.trim().length === 0) {
      console.error('[workshopsApi.upsertWorkshop] Invalid studio_id:', { studioId, type: typeof studioId });
      return { 
        data: null, 
        error: new Error(`Invalid studio_id: "${studioId}". Expected non-empty string.`), 
        mode: 'error' 
      };
    }

    // ✅ VERIFY STUDIO EXISTS IN DATABASE (prevent FK failures)
    const { data: studioExists, error: studioCheckErr } = await supabase
      .from('studios')
      .select('id')
      .eq('id', studioId)
      .limit(1);
    
    if (studioCheckErr) {
      console.error('[workshopsApi.upsertWorkshop] Error checking if studio exists:', { studioId, error: studioCheckErr?.message });
      return {
        data: null,
        error: new Error(`Failed to verify studio ${studioId} exists: ${studioCheckErr?.message}`),
        mode: 'error'
      };
    }

    if (!studioExists || studioExists.length === 0) {
      console.error('[workshopsApi.upsertWorkshop] Studio does NOT exist in database - FK will fail!', { studioId, studioExists: studioExists?.length || 0 });
      return {
        data: null,
        error: new Error(`Studio with ID "${studioId}" not found in public.studios. Please check your studio ID or create your studio profile first.`),
        mode: 'error'
      };
    }

    // 🔒 CODE GUARD: Enforce insert vs. update logic
    // This prevents accidental column name bugs by catching mode mismatches at runtime
    const remoteId = local?.networkId || local?.remoteId || local?.id_remote;
    
    if (remoteId && !local?.id) {
      // ❌ ERROR: remoteId exists (update mode) but local.id missing (looks like insert)
      console.error('[workshopsApi.upsertWorkshop] Mode mismatch detected:', { remoteId, hasId: !!local?.id });
      return {
        data: null,
        error: new Error(
          `[CODE GUARD] Mode mismatch: remoteId exists (${remoteId}) but workshop.id is missing. ` +
          `This indicates a data integrity issue. Verify workshop object has both id and remote_id set.`
        ),
        mode: 'error'
      };
    }

    if (!remoteId && local?.id) {
      // ⚠️ WARNING: local.id exists (looks like update) but remoteId missing (should insert)
      // Log warning but allow - this can happen when duplicating workshops
      console.warn(
        '[workshopsApi.upsertWorkshop] Mode mismatch warning: local.id exists but remoteId missing.',
        `Treating as INSERT (new workshop). If this was meant to be an UPDATE, ` +
        `ensure workshop.networkId, remoteId, or id_remote is set.`,
        { local_id: local?.id, remoteId }
      );
    }
    if (remoteId) {
      const { data, error } = await updateWorkshop(remoteId, payload);
      if (error) return { data: null, error, mode: 'update' };

      const publishResult = await setWorkshopPublish(remoteId, isPublished);
      if (publishResult.error) {
        return { data: null, error: publishResult.error, mode: 'update' };
      }

      return { data, error: null, mode: 'update' };
    } else {
      // Use createWorkshopForStudio for insert (will set studio_id)
      const createPayload = { ...payload };
      // Remove studio_id from create payload - helper adds automatically
      delete (createPayload as any).studio_id;
      const { data, error } = await createWorkshopForStudio(studioId, createPayload);
      if (error) return { data: null, error, mode: 'create' };

      if (data?.id) {
        const publishResult = await setWorkshopPublish(data.id, isPublished);
        if (publishResult.error) {
          return { data: null, error: publishResult.error, mode: 'create' };
        }
      }

      return { data, error: null, mode: 'create' };
    }
  } catch (error: any) {
    // RLS permission errors are expected when user doesn't have full studio membership permissions
    // Local save will still succeed, so log as warning
    if (error?.code === '42501' || error?.status === 403) {
      console.warn('[workshopsApi.upsertWorkshop] Supabase RLS permission denied (local save succeeded):', error?.message);
    } else {
      console.error('[workshopsApi.upsertWorkshop] Failed', error);
    }
    return { data: null, error, mode: 'error' };
  }
}

/**
 * Publish a studio workshop to the visitor world (public.workshops table)
 * 
 * Creates or updates a public workshop record that visitors can see.
 * Workflow: Studio Draft → Publish → Supabase → Visitor Discovery Page
 * 
 * Fields upserted:
 * - studio_id, title, description
 * - start_at, end_at
 * - price_cents, currency
 * - capacity, seats_taken (reset to 0)
 * - is_published (set to true)
 * - archived_at (set to null)
 * 
 * @param workshopId - Workshop UUID from studio workspace
 * @param workshopData - Workshop data { studio_id, title, description, start_at, end_at, price_cents, currency, capacity }
 * @returns { data, error } where data is the published workshop row with id
 * 
 * @example
 * const { data: publishedWorkshop } = await publishWorkshopToVisitors(
 *   'abc123',
 *   {
 *     studio_id: 'studio-xyz',
 *     title: 'Wheel Throwing 101',
 *     description: 'Learn the basics...',
 *     start_at: '2025-01-20T10:00:00Z',
 *     end_at: '2025-01-20T12:00:00Z',
 *     price_cents: 8000,
 *     currency: 'AUD',
 *     capacity: 12
 *   }
 * );
 * // publishedWorkshop.id is the remote workshop ID for future updates
 */
export async function publishWorkshopToVisitors(
  workshopId: string,
  workshopData: {
    studio_id: string;
    title: string;
    description?: string | null;
    start_at: string;
    end_at: string;
    price_cents: number;
    currency?: string;
    capacity: number;
  }
) {
  const queryDesc = `upsert into public.workshops where studio_id=${workshopData.studio_id} and id=${workshopId}`;
  
  try {
    // Get current session from centralized auth state (no direct supabase.auth call)
    const session = getCachedSession();

    if (!session) {
      throw new Error('No authenticated session - user must be logged in to publish workshops');
    }

    // Prepare payload for upsert
    const payload = {
      id: workshopId, // Use studio workshop ID as the remote ID
      studio_id: workshopData.studio_id,
      title: workshopData.title,
      description: workshopData.description || null,
      start_at: workshopData.start_at,
      end_at: workshopData.end_at,
      price_cents: Math.max(0, workshopData.price_cents || 0),
      currency: (workshopData.currency || 'AUD').toUpperCase(),
      capacity: Math.max(1, workshopData.capacity || 1),
      seats_taken: 0, // Reset seats when publishing (fresh visitor offer)
      is_published: true, // Explicitly published to visitors
      archived_at: null, // Ensure not archived
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Upsert into public.workshops table
    // This creates a new record if id doesn't exist, or updates if it does
    const { data, error } = await supabase
      .from('workshops')
      .upsert(payload, { onConflict: 'id' })
      .select()
      .single();

    if (error) {
      throw error;
    }

    console.log('[workshopsApi.publishWorkshopToVisitors] Successfully published workshop:', {
      workshopId,
      remoteWorkshopId: data?.id,
      title: data?.title,
    });

    return { data, error: null };
  } catch (error: any) {
    console.error('[workshopsApi.publishWorkshopToVisitors] Failed to publish workshop', {
      workshopId,
      query: queryDesc,
      error: error?.message || error,
    });
    return { data: null, error };
  }
}

export {
  getVisitorWorkshops as listPublicWorkshops,
  getStudioWorkshops as listWorkshopsForStudio,
  upsertWorkshop as syncLocalWorkshopToNetwork,
};
